// @flow
import React, {useState, useEffect} from 'react';
import {Link, Route, Switch} from 'react-router-dom';
import PropTypes from 'prop-types';

import {makeStyles, Typography} from '@material-ui/core';

import {
  AddCircleTwoTone,
  ArrowForwardIos,
  CheckCircle,
  RadioButtonUnchecked,
} from '@material-ui/icons';
import {colors} from '../../../../../assets/styles/colors';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {
  useDataShippingManual,
  useFetchShippingMethods,
  useResetState,
} from '../utils';
import LogisticsShippingManualEdit from './LogisticsShippingManualEdit';

export const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: 'white',
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
  },
  divider: {
    background: '#e8e8ed',
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  checkboxLabel: {
    color: colors.grey700,
    fontSize: '0.875rem',
    margin: theme.spacing(3, 0),
  },
  methodList: {
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #dbdbe0',
    paddingInline: theme.spacing(3),
    borderTop: 'none',
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
  },
  methodButton: {
    margin: theme.spacing(0, -3),
    borderRadius: '0px',
    padding: theme.spacing(2, 3),
    textAlign: 'left',
    justifyContent: 'flex-start',
    maxHeight: 76,
  },
  label: {
    fontWeight: 500,
    fontSize: '1rem',
    whiteSpace: 'break-spaces',
    color: colors.grey800,
    '&.disabled': {
      color: colors.grey500,
    },
  },
  secondLine: {
    whiteSpace: 'normal',
    color: colors.grey800,
    fontSize: '0.75rem',
    '&.disabled': {
      color: colors.grey500,
    },
  },
  startIcon: {
    marginRight: theme.spacing(2),
  },
  endIcon: {
    marginLeft: 'auto',
  },
  newMethodButton: {
    marginBottom: theme.spacing(3),
    width: '100%',
    border: `2px dashed ${colors.grey300}`,
    borderRadius: '8px',
    padding: theme.spacing(2),
    textAlign: 'left',
    justifyContent: 'flex-start',
    color: colors.grey700,
    fontWeight: 400,
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
}));

export default function LogisticsShippingManual() {
  const classes = useStyles();

  const {shippingMethods: shippingMethod} = useFetchShippingMethods();
  const {shippingDataManual} = useDataShippingManual();
  const {resetState} = useResetState();

  const [enabled, setEnabled] = useState(
    shippingMethod &&
      shippingMethod.enabled &&
      shippingMethod.storeShippingMethodSettings.storeShippingMethodType ===
        'MANUAL'
      ? true
      : false
  );

  useEffect(() => {
    if (
      shippingMethod &&
      shippingMethod.enabled &&
      shippingMethod.storeShippingMethodSettings &&
      shippingMethod.storeShippingMethodSettings.storeShippingMethodType ===
        'FLAT_RATE'
    )
      setEnabled(true);
  }, [shippingMethod]);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  const checkedCheckbox = (
    <CheckCircle color="primary" style={{fontSize: '1.75rem'}} />
  );
  const uncheckedCheckbox = (
    <RadioButtonUnchecked style={{fontSize: '1.75rem'}} />
  );

  return (
    <div className={classes.root}>
      <Switch>
        <Route exact path="/configure/logistics/shipping/manual">
          {/* <Divider className={classes.divider} /> */}
          <div className={classes.methodList}>
            {shippingMethod &&
              shippingDataManual &&
              shippingDataManual &&
              shippingDataManual.map((method, idx: number) => (
                <React.Fragment key={`methodButton-${idx}`}>
                  <CustomButton
                    classes={{
                      root: classes.methodButton,
                      startIcon: classes.startIcon,
                      endIcon: classes.endIcon,
                    }}
                    color="default"
                    startIcon={
                      method.enabled ? checkedCheckbox : uncheckedCheckbox
                    }
                    endIcon={
                      <ArrowForwardIos
                        style={{
                          fontSize: '1rem',
                          color: !enabled ? colors.grey300 : undefined,
                        }}
                      />
                    }
                    component={Link}
                    to={`/configure/logistics/shipping/manual/${method.id}`}
                  >
                    <div>
                      <Typography
                        className={`${classes.label} 
                        ${!enabled ? '' : ''}
                        `}
                      >
                        {method.shippingRates[0].name}
                      </Typography>
                    </div>
                  </CustomButton>
                </React.Fragment>
              ))}

            <CustomButton
              classes={{
                root:
                  shippingDataManual && shippingDataManual.length > 0
                    ? classes.newMethodButton
                    : classes.newMethodButton + ' ' + classes.marginTop,
                endIcon: classes.endIcon,
              }}
              color="default"
              endIcon={
                <AddCircleTwoTone
                  style={{
                    fontSize: '1.5rem',
                    color: enabled ? colors.purple3 : colors.purple1,
                  }}
                />
              }
              component={Link}
              to={`/configure/logistics/shipping/manual/new${window.location.search}`}
            >
              Añadir Nuevo
            </CustomButton>
          </div>
        </Route>
        <Route
          path="/configure/logistics/shipping/manual/:flatRateId"
          component={LogisticsShippingManualEdit}
        />
      </Switch>
    </div>
  );
}

LogisticsShippingManual.propTypes = {
  store: PropTypes.object,
  shippingMethod: PropTypes.object,
  saveShippingMethod: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  savingSuccess: PropTypes.bool,
  resetSavedSuccess: PropTypes.func.isRequired,
};
